import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, CheckCircle2, Circle, Flag, Mail, Pencil, X, Check,
  ListTodo, AlertTriangle, StickyNote, CalendarDays,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITIES = {
  high: { label: "High", dot: "bg-red-500", ring: "ring-red-500/30", text: "text-red-600" },
  normal: { label: "Normal", dot: "bg-slate-400", ring: "ring-slate-400/30", text: "text-slate-500" },
  low: { label: "Low", dot: "bg-sky-500", ring: "ring-sky-500/30", text: "text-sky-600" },
};

const todayStr = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
const parseLocal = (s) => { if (!s) return null; const [y, m, d] = s.slice(0, 10).split("-").map(Number); return new Date(y, m - 1, d); };
const fmtDue = (s) => { const d = parseLocal(s); return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""; };
const toKey = (date) => date.toLocaleDateString("en-CA");

function TaskItem({ t, onToggle, onDelete, onSave, busy }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(t.title);
  const [notes, setNotes] = useState(t.notes || "");
  const [due, setDue] = useState(t.due_date || "");
  const [priority, setPriority] = useState(t.priority || "normal");
  const [confirm, setConfirm] = useState(false);

  const p = PRIORITIES[t.priority] || PRIORITIES.normal;
  const overdue = !t.completed && t.due_date && t.due_date.slice(0, 10) < todayStr();
  const dueToday = !t.completed && t.due_date && t.due_date.slice(0, 10) === todayStr();

  const save = () => { onSave(t.id, { title: title.trim() || t.title, notes, due_date: due || null, priority }); setEditing(false); };

  if (editing) {
    const inp = "w-full px-3 py-2 rounded-lg bg-white border border-[#E7E5DF] text-sm focus:outline-none focus:border-emerald-600/60";
    return (
      <div className="rounded-2xl border border-emerald-300 bg-white p-3 space-y-2" data-testid={`task-edit-form-${t.id}`}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inp} data-testid={`task-edit-title-${t.id}`} />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes…" className={`${inp} resize-none`} data-testid={`task-edit-notes-${t.id}`} />
        <div className="flex flex-wrap gap-2">
          <input type="date" value={due ? due.slice(0, 10) : ""} onChange={(e) => setDue(e.target.value)} className={`${inp} flex-1 min-w-[140px]`} data-testid={`task-edit-due-${t.id}`} />
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className={`${inp} flex-1 min-w-[120px]`} data-testid={`task-edit-priority-${t.id}`}>
            {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={save} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700" data-testid={`task-save-${t.id}`}><Check size={13} /> Save</button>
          <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E7E5DF] text-slate-500 text-xs font-semibold"><X size={13} /> Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`group rounded-2xl border bg-white p-3.5 flex items-start gap-3 transition-colors ${t.completed ? "border-[#E7E5DF] opacity-60" : overdue ? "border-red-200 bg-red-50/40" : "border-[#E7E5DF] hover:border-emerald-300"}`} data-testid={`task-item-${t.id}`}>
      <button onClick={() => onToggle(t)} disabled={busy} className="mt-0.5 shrink-0 text-emerald-600 disabled:opacity-40" data-testid={`task-toggle-${t.id}`} title={t.completed ? "Mark active" : "Mark done"}>
        {t.completed ? <CheckCircle2 size={22} /> : <Circle size={22} className="text-slate-300 hover:text-emerald-500 transition-colors" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${p.dot}`} title={`${p.label} priority`} />
          <p className={`text-sm font-semibold leading-snug ${t.completed ? "line-through text-slate-400" : "text-[#0B0F14]"}`}>{t.title}</p>
        </div>
        {t.notes && <p className="mt-1 ml-4 text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">{t.notes}</p>}
        {t.due_date && (
          <span className={`mt-1.5 ml-4 inline-flex items-center gap-1 text-[11px] font-semibold ${overdue ? "text-red-600" : dueToday ? "text-emerald-700" : "text-slate-500"}`}>
            {overdue && <AlertTriangle size={11} />}<CalendarDays size={11} /> {fmtDue(t.due_date)}{overdue ? " · overdue" : dueToday ? " · today" : ""}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg text-slate-300 hover:text-emerald-600 hover:bg-emerald-50 transition-colors md:opacity-0 md:group-hover:opacity-100" data-testid={`task-edit-${t.id}`}><Pencil size={14} /></button>
        {confirm ? (
          <>
            <button onClick={() => onDelete(t)} className="p-1.5 rounded-lg text-white bg-red-600 hover:bg-red-700" data-testid={`task-delete-confirm-${t.id}`}><Trash2 size={14} /></button>
            <button onClick={() => setConfirm(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={14} /></button>
          </>
        ) : (
          <button onClick={() => setConfirm(true)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors md:opacity-0 md:group-hover:opacity-100" data-testid={`task-delete-${t.id}`}><Trash2 size={14} /></button>
        )}
      </div>
    </div>
  );
}

export default function TasksPanel({ token, onExpire }) {
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("active");
  const [selectedDay, setSelectedDay] = useState(null);
  const [emailing, setEmailing] = useState(false);

  // add form
  const [nt, setNt] = useState("");
  const [nDue, setNDue] = useState("");
  const [nPrio, setNPrio] = useState("normal");
  const [nNotes, setNNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [adding, setAdding] = useState(false);

  // memo
  const [memo, setMemo] = useState("");
  const [memoState, setMemoState] = useState("idle"); // idle | saving | saved
  const memoTimer = useRef(null);
  const memoLoaded = useRef(false);

  const authHdr = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, mr] = await Promise.all([
        axios.get(`${API}/admin/tasks`, authHdr),
        axios.get(`${API}/admin/memo`, authHdr),
      ]);
      setTasks(tr.data);
      setMemo(mr.data.text || "");
      memoLoaded.current = true;
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Failed to load tasks.");
    } finally { setLoading(false); }
  }, [authHdr, onExpire]);

  useEffect(() => { load(); }, [load]);

  const addTask = async () => {
    if (!nt.trim()) return;
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/admin/tasks`, { title: nt.trim(), due_date: nDue || null, priority: nPrio, notes: nNotes }, authHdr);
      setTasks((p) => [...(p || []), data]);
      setNt(""); setNDue(""); setNPrio("normal"); setNNotes(""); setShowNotes(false);
      toast.success("Task added");
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Could not add task.");
    } finally { setAdding(false); }
  };

  const toggle = async (t) => {
    setBusy(t.id);
    try {
      const { data } = await axios.patch(`${API}/admin/tasks/${t.id}`, { completed: !t.completed }, authHdr);
      setTasks((p) => p.map((x) => (x.id === t.id ? data : x)));
    } catch (e) { if (e.response?.status === 401) onExpire?.(); else toast.error("Update failed."); }
    finally { setBusy(null); }
  };

  const saveTask = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/admin/tasks/${id}`, patch, authHdr);
      setTasks((p) => p.map((x) => (x.id === id ? data : x)));
      toast.success("Task updated");
    } catch (e) { if (e.response?.status === 401) onExpire?.(); else toast.error("Update failed."); }
  };

  const del = async (t) => {
    const snap = { ...t };
    try {
      await axios.delete(`${API}/admin/tasks/${t.id}`, authHdr);
      setTasks((p) => p.filter((x) => x.id !== t.id));
      toast.success("Task deleted", { duration: 6000, action: { label: "Undo", onClick: async () => {
        const { data } = await axios.post(`${API}/admin/tasks`, { title: snap.title, notes: snap.notes, due_date: snap.due_date, priority: snap.priority }, authHdr);
        setTasks((p) => [...(p || []), data]);
      } } });
    } catch (e) { if (e.response?.status === 401) onExpire?.(); else toast.error("Delete failed."); }
  };

  const emailList = async () => {
    setEmailing(true);
    try {
      const { data } = await axios.post(`${API}/admin/tasks/send-digest`, {}, authHdr);
      toast.success(data.sent ? `Emailed your list (${data.pending} pending)` : "No pending tasks to email");
    } catch (e) { if (e.response?.status === 401) onExpire?.(); else toast.error("Could not send email."); }
    finally { setEmailing(false); }
  };

  const onMemoChange = (v) => {
    setMemo(v);
    setMemoState("saving");
    clearTimeout(memoTimer.current);
    memoTimer.current = setTimeout(async () => {
      try { await axios.put(`${API}/admin/memo`, { text: v }, authHdr); setMemoState("saved"); setTimeout(() => setMemoState("idle"), 1500); }
      catch { setMemoState("idle"); }
    }, 700);
  };

  const stats = useMemo(() => {
    const list = tasks || [];
    const pending = list.filter((t) => !t.completed);
    const overdue = pending.filter((t) => t.due_date && t.due_date.slice(0, 10) < todayStr());
    const done = list.filter((t) => t.completed);
    return { pending: pending.length, overdue: overdue.length, done: done.length, total: list.length };
  }, [tasks]);

  const dueDates = useMemo(() => (tasks || []).filter((t) => !t.completed && t.due_date).map((t) => parseLocal(t.due_date)).filter(Boolean), [tasks]);
  const overdueDates = useMemo(() => (tasks || []).filter((t) => !t.completed && t.due_date && t.due_date.slice(0, 10) < todayStr()).map((t) => parseLocal(t.due_date)).filter(Boolean), [tasks]);

  const visible = useMemo(() => {
    let list = [...(tasks || [])];
    if (selectedDay) {
      const key = toKey(selectedDay);
      list = list.filter((t) => t.due_date && t.due_date.slice(0, 10) === key);
    } else if (filter === "active") list = list.filter((t) => !t.completed);
    else if (filter === "completed") list = list.filter((t) => t.completed);
    else if (filter === "overdue") list = list.filter((t) => !t.completed && t.due_date && t.due_date.slice(0, 10) < todayStr());
    // sort: incomplete first, then by due date, then priority
    const prioRank = { high: 0, normal: 1, low: 2 };
    return list.sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const ad = a.due_date || "9999"; const bd = b.due_date || "9999";
      if (ad !== bd) return ad < bd ? -1 : 1;
      return (prioRank[a.priority] ?? 1) - (prioRank[b.priority] ?? 1);
    });
  }, [tasks, filter, selectedDay]);

  const filterBtn = (id, label, count) => (
    <button onClick={() => { setFilter(id); setSelectedDay(null); }} data-testid={`task-filter-${id}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${filter === id && !selectedDay ? "bg-[#0E3B3B] text-white" : "bg-white border border-[#E7E5DF] text-slate-600 hover:border-emerald-500"}`}>
      {label}{count != null && <span className={`grid place-items-center min-w-4 h-4 px-1 rounded-full text-[10px] ${filter === id && !selectedDay ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>}
    </button>
  );

  const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60";

  if (loading && !tasks) return <div className="grid place-items-center py-20 text-slate-500"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="tasks-panel">
      {/* Header + stats + email */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-emerald-600/10 text-emerald-700 shrink-0"><ListTodo size={18} /></span>
          <div>
            <h2 className="font-display font-bold text-lg text-[#0B0F14] leading-tight">To-Do Board</h2>
            <p className="text-xs text-slate-500">{stats.pending} pending{stats.overdue ? ` · ${stats.overdue} overdue` : ""} · {stats.done} done</p>
          </div>
        </div>
        <button onClick={emailList} disabled={emailing} data-testid="task-email-list"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0E3B3B] text-white text-sm font-semibold hover:bg-[#0A2C2C] transition-colors disabled:opacity-60">
          {emailing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email me my list
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-5 items-start">
        {/* LEFT: add + list */}
        <div className="space-y-4">
          {/* Add task */}
          <div className="rounded-2xl border border-[#E7E5DF] bg-white p-3.5 shadow-soft" data-testid="task-add-card">
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={nt} onChange={(e) => setNt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()}
                placeholder="Add a task…" className={`${inputCls} flex-1`} data-testid="task-add-title" />
              <div className="flex gap-2">
                <input type="date" value={nDue} onChange={(e) => setNDue(e.target.value)} className={`${inputCls} flex-1 sm:w-[150px]`} data-testid="task-add-due" title="Due date" />
                <select value={nPrio} onChange={(e) => setNPrio(e.target.value)} className={`${inputCls} sm:w-[120px]`} data-testid="task-add-priority">
                  {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <button onClick={addTask} disabled={adding || !nt.trim()} data-testid="task-add-submit"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50">
                {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={16} />} Add
              </button>
            </div>
            {showNotes ? (
              <textarea value={nNotes} onChange={(e) => setNNotes(e.target.value)} rows={2} placeholder="Add notes (optional)…"
                className={`${inputCls} mt-2 resize-none`} data-testid="task-add-notes" />
            ) : (
              <button onClick={() => setShowNotes(true)} className="mt-2 text-xs text-slate-400 hover:text-emerald-600">+ Add notes</button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {selectedDay ? (
              <button onClick={() => setSelectedDay(null)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold" data-testid="task-clear-day">
                <CalendarDays size={13} /> {selectedDay.toLocaleDateString("en-US", { month: "short", day: "numeric" })} <X size={13} />
              </button>
            ) : (
              <>
                {filterBtn("active", "Active", stats.pending)}
                {filterBtn("overdue", "Overdue", stats.overdue)}
                {filterBtn("completed", "Done", stats.done)}
                {filterBtn("all", "All", stats.total)}
              </>
            )}
          </div>

          {/* List */}
          {visible.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E7E5DF] bg-white p-10 text-center text-slate-400 text-sm" data-testid="tasks-empty">
              {selectedDay ? "No tasks due on this day." : filter === "completed" ? "No completed tasks yet." : "You're all caught up — add a task above."}
            </div>
          ) : (
            <div className="space-y-2.5">
              {visible.map((t) => <TaskItem key={t.id} t={t} onToggle={toggle} onDelete={del} onSave={saveTask} busy={busy === t.id} />)}
            </div>
          )}
        </div>

        {/* RIGHT: calendar + memo */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-[#E7E5DF] bg-white p-3 shadow-soft" data-testid="tasks-calendar">
            <Calendar
              mode="single"
              selected={selectedDay}
              onSelect={(d) => setSelectedDay(d || null)}
              modifiers={{ due: dueDates, overdue: overdueDates }}
              modifiersClassNames={{
                due: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-emerald-500",
                overdue: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-red-500",
              }}
              className="w-full [&_.rdp-day_selected]:bg-emerald-600 [&_.rdp-day_selected]:text-white"
            />
            <div className="flex items-center gap-4 px-2 pb-1 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Due</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Overdue</span>
            </div>
          </div>

          <div className="rounded-2xl border border-[#E7E5DF] bg-white p-4 shadow-soft" data-testid="tasks-memo">
            <div className="flex items-center justify-between mb-2">
              <p className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-wider text-slate-400"><StickyNote size={13} /> Memo / Scratchpad</p>
              <span className="text-[10px] text-slate-400" data-testid="memo-save-indicator">
                {memoState === "saving" ? "Saving…" : memoState === "saved" ? "Saved ✓" : ""}
              </span>
            </div>
            <textarea value={memo} onChange={(e) => onMemoChange(e.target.value)} rows={6} placeholder="Jot down anything — ideas, reminders, phone numbers…"
              className="w-full px-3 py-2.5 rounded-xl bg-[#FBFAF8] border border-[#E7E5DF] text-sm text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60 resize-none leading-relaxed" data-testid="memo-textarea" />
          </div>
        </div>
      </div>
    </div>
  );
}
